import * as THREE from "three";
import { activeCanvasPointers } from "./input";
import {
  COMPASS_X,
  COMPASS_Z,
  HALF_L,
  HALF_W,
  SOUTH_WORD,
} from "./config";

const HEIGHT = 120;
const MIN_ZOOM = 0.55;
const MAX_ZOOM = 8;
const BASE_SPEED = 28;
const SPRINT = 2.4;
const PAD = 14;

export class PlanCamera {
  readonly camera: THREE.OrthographicCamera;
  enabled = false;

  private canvas: HTMLCanvasElement;
  private keys = new Set<string>();
  private zoom = 1;
  private halfW = 20;
  private halfH = 20;
  private center = new THREE.Vector3();
  private pan = new THREE.Vector3();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.camera = new THREE.OrthographicCamera(-20, 20, 20, -20, 0.1, 400);
    this.camera.up.set(0, 0, 1);
    this.bind();
  }

  frame(aspect: number, extraPad = 0): void {
    const pad = PAD + extraPad;
    const minX = Math.min(-HALF_W, COMPASS_X, SOUTH_WORD.x) - pad;
    const maxX = Math.max(HALF_W, COMPASS_X, SOUTH_WORD.x) + pad;
    const minZ = Math.min(-HALF_L, COMPASS_Z, SOUTH_WORD.z) - pad;
    const maxZ = Math.max(HALF_L, COMPASS_Z, SOUTH_WORD.z) + pad;
    this.center.set((minX + maxX) / 2, 0, (minZ + maxZ) / 2);
    const worldW = maxX - minX;
    const worldH = maxZ - minZ;
    if (worldW / worldH > aspect) {
      this.halfW = worldW / 2;
      this.halfH = this.halfW / aspect;
    } else {
      this.halfH = worldH / 2;
      this.halfW = this.halfH * aspect;
    }
    this.zoom = 1;
    this.apply();
  }

  setAspect(aspect: number): void {
    this.halfW = this.halfH * aspect;
    this.apply();
  }

  panBy(dx: number, dy: number): void {
    if (!this.enabled) return;
    const spanX = (this.halfW * 2) / this.zoom;
    const spanZ = (this.halfH * 2) / this.zoom;
    this.center.x += (dx / this.canvas.clientWidth) * spanX;
    this.center.z += (dy / this.canvas.clientHeight) * spanZ;
    this.apply();
  }

  zoomBy(factor: number): void {
    if (!this.enabled) return;
    this.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.zoom * factor));
    this.apply();
  }

  zoomAt(factor: number, sx: number, sy: number): void {
    if (!this.enabled) return;
    const before = this.screenToWorld(sx, sy);
    this.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.zoom * factor));
    const after = this.screenToWorld(sx, sy);
    this.center.x += before.x - after.x;
    this.center.z += before.z - after.z;
    this.apply();
  }

  update(dt: number): void {
    if (!this.enabled) return;
    const sprint = this.keys.has("ShiftLeft") || this.keys.has("ShiftRight");
    const speed = (BASE_SPEED / this.zoom) * (sprint ? SPRINT : 1);
    const north = this.keys.has("KeyW") || this.keys.has("ArrowUp") ? 1 : 0;
    const south = this.keys.has("KeyS") || this.keys.has("ArrowDown") ? 1 : 0;
    const east = this.keys.has("KeyD") || this.keys.has("ArrowRight") ? 1 : 0;
    const west = this.keys.has("KeyA") || this.keys.has("ArrowLeft") ? 1 : 0;
    this.pan.set(east - west, 0, north - south);
    if (this.pan.lengthSq() > 0) {
      this.pan.normalize().multiplyScalar(speed * dt);
      this.center.add(this.pan);
      this.apply();
    }
  }

  private bind(): void {
    const c = this.canvas;

    c.addEventListener(
      "wheel",
      (e) => {
        if (!this.enabled) return;
        e.preventDefault();
        if (activeCanvasPointers() > 0) return;
        if (e.ctrlKey) {
          this.zoomBy(Math.exp(-e.deltaY * 0.01));
          return;
        }
        const next = this.zoom * (e.deltaY > 0 ? 0.9 : 1.12);
        this.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
        this.apply();
      },
      { passive: false },
    );

    window.addEventListener("keydown", (e) => {
      if (!this.enabled) return;
      this.keys.add(e.code);
    });
    window.addEventListener("keyup", (e) => this.keys.delete(e.code));
    window.addEventListener("blur", () => this.keys.clear());
  }

  private screenToWorld(sx: number, sy: number): { x: number; z: number } {
    const spanX = (this.halfW * 2) / this.zoom;
    const spanZ = (this.halfH * 2) / this.zoom;
    return {
      x: this.center.x + (sx / this.canvas.clientWidth - 0.5) * spanX,
      z: this.center.z - (sy / this.canvas.clientHeight - 0.5) * spanZ,
    };
  }

  private apply(): void {
    const w = this.halfW / this.zoom;
    const h = this.halfH / this.zoom;
    this.camera.left = -w;
    this.camera.right = w;
    this.camera.top = h;
    this.camera.bottom = -h;
    this.camera.position.set(this.center.x, HEIGHT, this.center.z);
    this.camera.lookAt(this.center.x, 0, this.center.z);
    this.camera.updateProjectionMatrix();
  }
}
